import { Box, Flex, Stack, Tooltip } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { Badge, Icon, Text } from '@repo/foundations';
import { memo, useCallback } from 'react';
import { useAuthUser } from '../../../api';
import { MetricsOverviewCard } from '../../Overview/components/MetricsOverviewCard';
import { ClockIcon, CodeIcon } from '../../Overview/components/icons';
import type { AiMetricsCardsProps } from '../types';

const MetricTooltip = memo(
	({
		title,
		description,
		metricType,
		dateFilter,
	}: {
		title: string;
		description: string;
		metricType: 'total_queries' | 'total_time_saved_hours';
		dateFilter?: string;
	}) => {
		const { user, workspace } = useAuthUser();

		const handleTooltipOpen = useCallback(() => {
			trackEvent(
				'ai_impact_dashboard/metric_tooltip_hovered',
				{
					metric_type: metricType,
					tooltip_type: 'value',
					current_date_filter: dateFilter ?? '30d',
				},
				user,
				workspace
			);
		}, [metricType, dateFilter, user, workspace]);

		return (
			<Tooltip
				position="bottom"
				label={
					<Stack
						px="sm"
						py="xs"
						maw={420}
						w={420}
						style={{ whiteSpace: 'pre-wrap' }}
						spacing="xs"
					>
						<Text weight="bold" size="sm">
							{title}
						</Text>
						<Text size="sm">{description}</Text>
					</Stack>
				}
				style={{ maxWidth: 420 }}
			>
				<Box onMouseEnter={handleTooltipOpen}>
					<Icon name="infoCircle" color="text/secondary/default" />
				</Box>
			</Tooltip>
		);
	}
);

export const AiMetricsCards = memo(
	({
		totalQueries,
		totalTimeSavedHours,
		totalQueriesPercentageChange,
		totalTimeSavedHoursPercentageChange,
		totalQueriesMultiplier,
		totalTimeSavedHoursMultiplier,
		onTotalQueriesClick,
		onTotalTimeSavedClick,
		dateFilter,
	}: AiMetricsCardsProps) => {
		const formattedTotalTimeSavedHours = totalTimeSavedHours.toFixed(0);
		const totalTimeStr = `${formattedTotalTimeSavedHours} hr${formattedTotalTimeSavedHours === '1' ? '' : 's'}`;

		const createPercentageBadge = (
			percentageChange: number | null | undefined,
			multiplier: number | null | undefined
		) => {
			// If we have a multiplier, show that instead (only for increases)
			if (multiplier !== null && multiplier !== undefined && multiplier >= 2) {
				const formattedMultiplier = `${multiplier.toFixed(1)}x`;
				return <Badge variant="success">{formattedMultiplier}</Badge>;
			}

			// Show percentages for changes ≥5% in either direction (backend filters this)
			if (
				percentageChange === null ||
				percentageChange === undefined ||
				Math.abs(percentageChange) < 1
			)
				return null;

			// Format percentage with appropriate sign
			const formattedPercentage =
				percentageChange > 0
					? `+${percentageChange.toFixed(0)}%`
					: `${percentageChange.toFixed(0)}%`;

			// Use warning variant (red) for decreases, success variant (green) for increases
			const variant = percentageChange < 0 ? 'warning' : 'success';

			return <Badge variant={variant}>{formattedPercentage}</Badge>;
		};

		return (
			<Flex w="100%" justify="space-between" align="center" gap="md">
				<MetricsOverviewCard
					title="Total AI chats"
					value={totalQueries ?? 0}
					icon={CodeIcon}
					badge={createPercentageBadge(
						totalQueriesPercentageChange,
						totalQueriesMultiplier
					)}
					tooltip={
						<MetricTooltip
							title="Total AI chats"
							description="Tracks the total number of AI chats in your workspace."
							metricType="total_queries"
							dateFilter={dateFilter}
						/>
					}
					onClick={onTotalQueriesClick}
				/>
				<MetricsOverviewCard
					title="Total time savings"
					value={totalTimeStr}
					icon={ClockIcon}
					badge={createPercentageBadge(
						totalTimeSavedHoursPercentageChange,
						totalTimeSavedHoursMultiplier
					)}
					tooltip={
						<MetricTooltip
							title="Time savings"
							description="Estimates the manual effort your query would have required. Calculations are based on query complexity, data sources, and task requirements. (Time savings tracking started on November 4th, 2025)."
							metricType="total_time_saved_hours"
							dateFilter={dateFilter}
						/>
					}
					onClick={onTotalTimeSavedClick}
				/>
			</Flex>
		);
	}
);
